// WebSocket hook for managing connection to World Durable Object

import { useEffect, useRef, useCallback } from 'react';
import { useActiveWorldStore, SimulationStatus } from '../store';
import { ServerMessage, Message } from '../types';
import { generateId } from '../utils';
import { createWsTicket, ApiError } from '../api';

/**
 * One-line friendly sentence per error code. Shown as the primary line in
 * the inline ErrorNotice callout. Technical bits (code, provider, model,
 * raw message) live under the "Details" disclosure.
 */
const FRIENDLY_ERROR_TITLE: Record<string, string> = {
  RATE_LIMITED: "You're sending messages faster than the model can keep up. Try again in a moment.",
  CONTEXT_LENGTH_EXCEEDED: "The story has grown past the model's memory window. Start a new chapter or trim earlier messages.",
  CONTENT_FILTERED: "The model declined this content. Try rephrasing, or choose a different model in World settings.",
  INVALID_REQUEST: "The request wasn't shaped how the model expected.",
  INVALID_API_KEY: "An API key isn't valid. Check server configuration.",
  SERVICE_UNAVAILABLE: "The model provider is having trouble right now. Try again in a moment.",
  TIMEOUT: "The response took too long to arrive.",
  BUDGET_EXCEEDED: "This world has hit its budget for the session.",
  ALL_MODELS_FAILED: "No configured model could handle this turn.",
  INSUFFICIENT_CREDITS: "You're out of credits. Add credits to continue.",
  CAPACITY_EXCEEDED: "The provider is at capacity right now. Try again in a moment.",
  MODEL_NOT_FOUND: "The selected model isn't available. Pick a different model in World settings.",
  UNKNOWN: "Something went wrong generating this response.",
};

function friendlyTitleFor(code: string): string {
  return FRIENDLY_ERROR_TITLE[code] ?? FRIENDLY_ERROR_TITLE.UNKNOWN;
}

interface UseWebSocketOptions {
  worldId: string;
  userId: string;
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: string) => void;
}

export function useWebSocket({ worldId, userId, onConnected, onDisconnected, onError }: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const messageIdRef = useRef(0);
  const isUnmountedRef = useRef(false);
  const worldIdRef = useRef(worldId);
  const streamingTimeoutRef = useRef<number | null>(null);
  
  // Keep worldId ref updated
  worldIdRef.current = worldId;

  const {
    setConnected,
    setStreaming,
    appendStreamContent,
    clearStreamContent,
    addMessage,
    setMessages,
    prependOlderMessages,
    setLoadingOlderHistory,
    setHasMoreHistory,
    updateMessage,
    removeMessage,
    setWorldState,
    setError,
    addEphemeralNotice,
    setSimulationStatus,
    setPendingInput,
    clearPendingInput,
    restoreLastInput,
    addSessionCost,
    setCreditsBalance,
  } = useActiveWorldStore();

  const getNextMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return `msg_${Date.now()}_${messageIdRef.current}`;
  }, []);

  const STREAMING_TIMEOUT_MS = 120_000; // 2 minutes with no server activity

  const clearStreamingTimeout = useCallback(() => {
    if (streamingTimeoutRef.current !== null) {
      window.clearTimeout(streamingTimeoutRef.current);
      streamingTimeoutRef.current = null;
    }
  }, []);

  const resetStreamingTimeout = useCallback(() => {
    clearStreamingTimeout();
    streamingTimeoutRef.current = window.setTimeout(() => {
      console.warn('[ws] Server activity timed out; requesting cancellation and resynchronizing');
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'stop_generation', id: getNextMessageId() }));
      }
      setSimulationStatus({ phase: 'generation', status: 'Connection stalled. Waiting for server confirmation...', icon: '⏳' });
      wsRef.current?.close(4000, 'Resynchronize generation status');
      addEphemeralNotice({
        kind: 'error',
        title: friendlyTitleFor('TIMEOUT'),
        errorDetails: {
          code: 'TIMEOUT',
          rawMessage: 'Response timed out before the model returned any content.',
        },
      });
    }, STREAMING_TIMEOUT_MS);
  }, [clearStreamingTimeout, setSimulationStatus, addEphemeralNotice, getNextMessageId]);

  const handleServerMessage = useCallback((message: ServerMessage) => {
    console.debug('[ws]', message.type);
    
    switch (message.type) {
      case 'connected':
        console.log('Connection confirmed:', message.payload);
        break;
        
      case 'world_state':
        // Update messages and characters from WebSocket
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const wsWorldState = message.payload as any;
        if (wsWorldState.recentMessages) {
          setMessages(wsWorldState.recentMessages);
        }
        // Update active characters (Bug 1 fix)
        if (wsWorldState.activeCharacters) {
          useActiveWorldStore.getState().setActiveCharacters(wsWorldState.activeCharacters, wsWorldState.currentLocation);
        }
        if (typeof wsWorldState.creditsBalanceMicros === 'number') {
          setCreditsBalance(wsWorldState.creditsBalanceMicros);
        }
        // Restore streaming/generation status from server on reconnect
        if (wsWorldState.generationStatus) {
          clearStreamContent();
          resetStreamingTimeout();
          setStreaming(true);
          setSimulationStatus({
            phase: wsWorldState.generationStatus.phase,
            status: wsWorldState.generationStatus.status,
            icon: wsWorldState.generationStatus.icon,
          });
        } else {
          // Server says no generation running — clear any stale local streaming state
          clearStreamingTimeout();
          clearStreamContent();
          setStreaming(false);
          setSimulationStatus(null);
        }
        // P1.2: Update companion profile from world_state
        if (wsWorldState.companionProfile) {
          useActiveWorldStore.getState().setCompanionProfile(wsWorldState.companionProfile);
        }
        // P2.1: Update companion state (mood, relationship) from world_state
        if (wsWorldState.companionState) {
          useActiveWorldStore.getState().setCompanionState(wsWorldState.companionState);
        }
        // Bootstrap rail world facts from initial connect payload.
        if (wsWorldState.worldFacts) {
          useActiveWorldStore.getState().setWorldFacts(wsWorldState.worldFacts);
        }
        // Bootstrap chapter list for the spine.
        if (Array.isArray(wsWorldState.chapters)) {
          useActiveWorldStore.getState().setChapters(wsWorldState.chapters);
        }
        if (typeof wsWorldState.currentChapterNumber === 'number') {
          useActiveWorldStore.getState().setCurrentChapterNumber(wsWorldState.currentChapterNumber);
        }
        break;

      case 'balance_update':
        // Credits balance changed after billing debit
        if (typeof message.payload?.creditsBalanceMicros === 'number') {
          setCreditsBalance(message.payload.creditsBalanceMicros as number);
        }
        break;

      case 'companion_state_update':
        // P2.1: Companion mood/relationship state updated after state extraction
        if (message.payload) {
          const stateUpdate = message.payload as { 
            mood: string | null; 
            moodIntensity: number | null;
            relationshipPhase: string | null;
            intimacyLevel: number | null;
          };
          useActiveWorldStore.getState().setCompanionState(stateUpdate);
        }
        break;
        
      case 'characters_updated':
        // Full roster broadcast whenever the DO reloads characters (e.g. after an extraction commit)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const charUpdate = message.payload as any;
        if (charUpdate.characters) {
          const store = useActiveWorldStore.getState();
          store.setActiveCharacters(charUpdate.characters, charUpdate.currentLocation);
          if (typeof charUpdate.currentChapterNumber === 'number' && charUpdate.currentLocation !== undefined) {
            store.setChapters(store.chapters.map(chapter => chapter.chapterNumber === charUpdate.currentChapterNumber
              ? { ...chapter, primaryLocation: charUpdate.currentLocation } : chapter));
          }
        }
        break;

      case 'scene_update_failed':
        addEphemeralNotice({ kind: 'error', title: 'Scene details could not be updated. Your story is saved.' });
        break;

      case 'world_facts_updated':
        // Push from entity_extract success — active_plot_thread, etc.
        if (message.payload) {
          const facts = message.payload as { activePlotThread: string | null };
          useActiveWorldStore.getState().setWorldFacts(facts);
        }
        break;

      case 'recent_beats_updated':
        // Push from chunk indexer when new event tags land for the
        // current turn. Drives the bottom-of-rail tag ticker.
        if (message.payload) {
          const beats = message.payload as {
            events: string[];
            turnSpan: { start: number; end: number };
          };
          useActiveWorldStore.getState().setRecentBeats(beats);
        }
        break;

      case 'chapter_change':
        // An accepted scene decision advanced the persisted chapter.
        // Updates currentChapterNumber and appends the new chapter
        // row. Closing-chapter status update lets the spine re-style
        // it as completed without a refetch.
        if (message.payload) {
          const change = message.payload as {
            newChapterNumber: number;
            location: string;
            closedChapterNumber: number | null;
          };
          const store = useActiveWorldStore.getState();
          store.setCurrentChapterNumber(change.newChapterNumber);
          store.upsertChapter({
            chapterNumber: change.newChapterNumber,
            title: `Chapter ${change.newChapterNumber}`,
            primaryLocation: change.location,
            status: 'active',
          });
          if (change.closedChapterNumber != null) {
            store.setChapterStatus(change.closedChapterNumber, 'pending_summary');
          }
        }
        break;

      case 'chapter_updated':
        if (message.payload) {
          useActiveWorldStore.getState().upsertChapter(message.payload as {
            chapterNumber: number; title: string; primaryLocation: string | null; status: string;
          });
        }
        break;

      case 'chapters_revised':
        if (message.payload) {
          const revised = message.payload as { currentChapterNumber: number; chapters: Array<{
            chapterNumber: number; title: string; primaryLocation: string | null; status: string;
          }> };
          const store = useActiveWorldStore.getState();
          store.setCurrentChapterNumber(revised.currentChapterNumber);
          store.setChapters(revised.chapters);
        }
        break;

      case 'recall_fired':
        // Push from buildPipelineInput when retrieve() surfaces older
        // chunks. Briefly glows the referenced chapter nodes — makes
        // "the system reached back" a visible cognitive act.
        if (message.payload) {
          const recall = message.payload as {
            refs: Array<{ chapter: number; turn: number; score: number }>;
          };
          const store = useActiveWorldStore.getState();
          const distinctChapters = Array.from(new Set(recall.refs.map((r) => r.chapter)));
          for (const ch of distinctChapters) {
            store.glowChapter(ch);
            // Fade glow after ~1.5s. Restraint: brief flash, not a
            // sustained highlight — the user looks, sees, moves on.
            setTimeout(() => store.clearChapterGlow(ch), 1500);
          }
        }
        break;
        
      case 'narrative_start':
        setStreaming(true);
        clearStreamContent();
        resetStreamingTimeout();
        // Check if this is buffered mode (all modes are now buffered via pipeline)
        const startPayload = message.payload as { 
          messageId?: string; 
          bufferedMode?: boolean;
        };
        if (startPayload.bufferedMode) {
          // Buffered mode - show simulation status UI
          setSimulationStatus({
            phase: 'context',
            status: 'Initializing simulation...',
            icon: '🎭',
          });
        }
        break;
      
      case 'simulation_status':
        // Buffered mode: update simulation status for nice UX
        const simStatusPayload = message.payload as {
          phase: 'context' | 'generation' | 'guardrails' | 'complete' | 'scaffolding' | 'preflight' | 'state' | 'postprocessing';
          status: string;
          icon: string;
          classification?: string;
        };
        setSimulationStatus({
          phase: simStatusPayload.phase,
          status: simStatusPayload.status,
          icon: simStatusPayload.icon,
          classification: simStatusPayload.classification,
        });
        resetStreamingTimeout();
        console.log(`[simulation] ${simStatusPayload.icon} ${simStatusPayload.status}`);
        break;
        
      case 'narrative_chunk':
        const chunk = message.payload as { content: string; isComplete: boolean };
        appendStreamContent(chunk.content);
        resetStreamingTimeout();
        break;
      
      case 'narrative_complete':
        // Buffered mode: complete narrative delivered at once
        const completePayload = message.payload as { 
          content: string; 
          messageId: string;
        };
        // Clear simulation status
        setSimulationStatus(null);
        // Add the complete message directly
        const bufferedMessage: Message = {
          id: completePayload.messageId || `msg_${Date.now()}`,
          world_id: worldIdRef.current,
          role: 'assistant',
          content: completePayload.content,
          created_at: new Date().toISOString(),
        };
        clearStreamContent();
        addMessage(bufferedMessage);
        console.log(`[buffered] Received complete narrative: ${completePayload.content.length} chars`);
        break;
        
      case 'narrative_end':
        clearStreamingTimeout();
        setStreaming(false);
        setSimulationStatus(null); // Clear simulation status
        clearStreamContent();
        // Get the actual message ID and inference_log_id from the server
        const endPayload = message.payload as { 
          messageId?: string; 
          inference_log_id?: string;
          bufferedMode?: boolean;
          guardrails?: {
            violationsDetected: number;
            violationsFixed: number;
            wasRewritten: boolean;
          };
          usage?: {
            promptTokens: number;
            completionTokens: number;
            totalTokens: number;
            costMicros: number;
          };
          creditsBalanceMicros?: number | null;
        };
        if (typeof endPayload.creditsBalanceMicros === 'number') {
          setCreditsBalance(endPayload.creditsBalanceMicros);
        }
        // Track session cost
        if (endPayload.usage) {
          addSessionCost(
            endPayload.usage.promptTokens,
            endPayload.usage.completionTokens,
            endPayload.usage.costMicros
          );
        }
        // Log guardrail info if present
        if (endPayload.guardrails && endPayload.guardrails.violationsDetected > 0) {
          console.log(`[guardrails] Detected: ${endPayload.guardrails.violationsDetected}, Fixed: ${endPayload.guardrails.violationsFixed}, Rewritten: ${endPayload.guardrails.wasRewritten}`);
        }
        if (endPayload.messageId && endPayload.inference_log_id) {
          const saved = useActiveWorldStore.getState().messages.find(m => m.id === endPayload.messageId);
          if (saved) updateMessage(saved.id, saved.content, endPayload.inference_log_id);
        }
        break;
        
      case 'narrative_refined':
        // Sculptor has refined the narrative - update the streaming content and last message
        const refinedPayload = message.payload as { 
          content: string; 
          originalLength: number; 
          refinedLength: number;
        };
        console.log(`[sculptor] Narrative refined: ${refinedPayload.originalLength} → ${refinedPayload.refinedLength} chars`);
        
        // Update the streaming content with refined version
        // This replaces what was streamed with the sculpted version
        const currentMessages = useActiveWorldStore.getState().messages;
        if (currentMessages.length > 0) {
          const lastMessage = currentMessages[currentMessages.length - 1];
          if (lastMessage.role === 'assistant') {
            // Update the last assistant message with refined content
            updateMessage(lastMessage.id, refinedPayload.content);
          }
        }
        // Also update streaming content in case narrative_end hasn't fired yet
        useActiveWorldStore.getState().setStreamingContent(refinedPayload.content);
        break;
        
      case 'state_update':
        console.debug('[ws] State updated');
        break;
        
      case 'error':
        clearStreamContent();
        clearStreamingTimeout();
        const error = message.payload as { code: string; message: string; phase?: string; model?: string; provider?: string };
        console.error('Received error:', error.code, error.message, error.phase, error.provider, error.model);
        // Restore the user's unsent input on ANY error so they can retry
        // with one tap. Previously only rate-limit / insufficient-credits
        // restored — users would lose their typed input on generic failures.
        const restored = restoreLastInput();
        if (restored) {
          console.log('[error] Restored input:', restored.content.substring(0, 50) + '...');
        } else {
          clearPendingInput();
        }
        // Inline ErrorNotice callout. Friendly sentence on top; raw
        // provider/model/code live behind the "Details" disclosure.
        addEphemeralNotice({
          kind: 'error',
          title: friendlyTitleFor(error.code),
          errorDetails: {
            code: error.code,
            phase: error.phase,
            provider: error.provider,
            model: error.model,
            rawMessage: error.message,
          },
        });
        setStreaming(false);
        break;
        
      case 'pong':
        break;
      
      case 'generation_stopping':
        setSimulationStatus({ phase: 'generation', status: 'Finishing cancellation or save...', icon: '⏳' });
        resetStreamingTimeout();
        break;

      case 'turn_interrupted': {
        // The server was restarted while answering the player's last input.
        // The input is saved; the server resumes it once on its own, and
        // asks the player to resubmit if it is interrupted again.
        const interrupted = message.payload as { messageId: string; resuming: boolean };
        addEphemeralNotice(interrupted.resuming
          ? { kind: 'info', title: 'Your last turn was interrupted before its response could be saved. Resuming it now.' }
          : {
            kind: 'error',
            title: 'Your last turn was interrupted twice before its response could be saved. Resubmit that message to try again.',
            errorDetails: { code: 'TURN_INTERRUPTED', rawMessage: `Message ${interrupted.messageId} has no saved response.` },
          });
        break;
      }

      case 'message_added':
        // Server-initiated message (e.g., initial_situation sent as user message)
        const addedMessage = (message.payload as { message: Message }).message;
        addMessage(addedMessage);
        break;
      
      // Message action handlers
      case 'message_updated':
        const updatedMsg = message.payload as { messageId: string; content: string };
        updateMessage(updatedMsg.messageId, updatedMsg.content);
        break;
        
      case 'message_deleted':
        const deletedMsg = message.payload as { messageId: string };
        removeMessage(deletedMsg.messageId);
        break;
      
      case 'older_messages': {
        const payload = message.payload as { messages: Message[]; hasMore: boolean };
        prependOlderMessages(payload.messages || []);
        setHasMoreHistory(Boolean(payload.hasMore));
        setLoadingOlderHistory(false);
        break;
      }

      case 'message_confirmed':
        // Server confirms user message was saved with the same ID
        const confirmedPayload = message.payload as { messageId: string; syncStatus: 'ok' | 'mismatch' };
        if (confirmedPayload.syncStatus !== 'ok') {
          console.error('[sync] Message ID mismatch detected:', confirmedPayload.messageId);
        }
        // Clear pending input since message was accepted
        clearPendingInput();
        break;
        
      default:
        console.log('Unknown message type:', message.type);
    }
  }, [setWorldState, setMessages, prependOlderMessages, setLoadingOlderHistory, setHasMoreHistory, updateMessage, removeMessage, setStreaming, clearStreamContent, appendStreamContent, addMessage, setError, clearPendingInput, restoreLastInput, setSimulationStatus, addSessionCost, setCreditsBalance, resetStreamingTimeout, clearStreamingTimeout]);

  // Main effect - runs once on mount per worldId
  useEffect(() => {
    isUnmountedRef.current = false;
    reconnectAttemptsRef.current = 0;
    
    const connect = async () => {
      // Don't connect if unmounted or already connected
      if (isUnmountedRef.current) return;
      if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      // Build WebSocket URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = import.meta.env.VITE_API_HOST || window.location.host;
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('[ws] No auth token found');
        addEphemeralNotice({
          kind: 'error',
          title: 'You need to sign in to connect to this world.',
          errorDetails: { code: 'AUTH_REQUIRED', rawMessage: 'No auth token in localStorage.' },
        });
        return;
      }

      // Track which phase we're in for diagnostic error messages
      let phase = 'ticket';

      try {
        console.log('[ws] Requesting ticket...');
        const ticketResponse = await createWsTicket(worldIdRef.current);
        console.log('[ws] Ticket acquired');

        // Re-check after async gap — StrictMode double-mount can race here
        if (isUnmountedRef.current) return;
        if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
          return;
        }

        phase = 'upgrade';
        const wsUrl = `${protocol}//${host}/ws/worlds/${worldIdRef.current}?ticket=${encodeURIComponent(ticketResponse.ticket)}`;
        console.log('[ws] Connecting to:', wsUrl);

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          if (isUnmountedRef.current) {
            ws.close();
            return;
          }
          console.log('[ws] Connected');
          setConnected(true);
          setError(null);
          reconnectAttemptsRef.current = 0;

          // Signal to server that client is ready to receive messages
          // Server defers world_state until this is received
          ws.send(JSON.stringify({
            type: 'client_ready',
            id: `ready_${Date.now()}`,
            payload: {}
          }));

          onConnected?.();
        };

        ws.onclose = (event) => {
          console.log(`[ws] Closed: code=${event.code} reason=${event.reason || '(none)'}`);
          wsRef.current = null;

          if (isUnmountedRef.current) return;

          setConnected(false);
          onDisconnected?.();

          // Only reconnect if not intentionally closed (code 1000) and under max attempts
          if (event.code !== 1000 && reconnectAttemptsRef.current < 5) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            console.log(`[ws] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1}/5)...`);
            reconnectTimeoutRef.current = window.setTimeout(() => {
              reconnectAttemptsRef.current += 1;
              connect();
            }, delay);
          } else if (reconnectAttemptsRef.current >= 5) {
            const detail = event.code ? `WebSocket closed with code ${event.code}.` : 'WebSocket disconnected.';
            addEphemeralNotice({
              kind: 'error',
              title: 'Connection lost and reconnect attempts were exhausted. Refresh the page to try again.',
              errorDetails: { code: 'CONNECTION_LOST', rawMessage: detail },
            });
            onError?.('Connection lost');
          }
        };

        ws.onerror = () => {
          console.error(`[ws] Error during ${phase} phase`);
        };

        ws.onmessage = (event) => {
          try {
            const message: ServerMessage = JSON.parse(event.data);
            handleServerMessage(message);
          } catch (error) {
            console.error('[ws] Failed to parse message:', error);
          }
        };
      } catch (error) {
        console.error(`[ws] Failed during ${phase} phase:`, error);
        if (error instanceof ApiError && error.code === 'email_not_verified') {
          addEphemeralNotice({
            kind: 'error',
            title: 'Verify your email address before you can interact with worlds.',
            errorDetails: {
              code: 'EMAIL_NOT_VERIFIED',
              rawMessage: error.message || 'Email verification required.',
            },
          });
          onError?.('Email verification required');
        } else {
          const detail = phase === 'ticket' ? 'ticket request failed' : 'WebSocket upgrade failed';
          addEphemeralNotice({
            kind: 'error',
            title: 'Connection failed. Try signing in again.',
            errorDetails: {
              code: 'CONNECTION_FAILED',
              phase,
              rawMessage: detail,
            },
          });
          onError?.(`Connection failed: ${detail}`);
        }
      }
    };

    // Initial connection
    connect();

    // Ping interval to keep connection alive
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping', id: `ping_${Date.now()}`, payload: {} }));
      }
    }, 30000);

    // When a backgrounded tab becomes visible and the socket dropped while
    // we weren't looking, reconnect immediately instead of waiting out the
    // exponential backoff (or worse, showing a dead UI forever if we've
    // already burned the 5 retry attempts).
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (isUnmountedRef.current) return;
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        return;
      }
      if (reconnectTimeoutRef.current !== null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      setError(null);
      console.log('[ws] Tab visible and socket dropped — reconnecting now');
      connect();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      isUnmountedRef.current = true;
      clearInterval(pingInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearStreamingTimeout();

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }

      setConnected(false);
    };
  }, [worldId]); // Only re-run if worldId changes

  const sendMessage = useCallback((type: string, payload: Record<string, unknown> = {}) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      console.error('WebSocket not connected');
      return false;
    }

    const message = {
      type,
      id: getNextMessageId(),
      payload,
    };

    wsRef.current.send(JSON.stringify(message));
    return true;
  }, [getNextMessageId]);

  const sendUserInput = useCallback((content: string) => {
    // Generate cryptographically-secure ID matching backend format
    const messageId = generateId('msg');
    const userMessage: Message = {
      id: messageId,
      world_id: worldIdRef.current,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    // Track pending input for error recovery (before optimistic add)
    setPendingInput(messageId, content);
    addMessage(userMessage);
    // Send messageId to backend so IDs stay synchronized
    return sendMessage('user_input', { content, messageId });
  }, [sendMessage, addMessage, setPendingInput]);

  const requestState = useCallback(() => {
    return sendMessage('request_state');
  }, [sendMessage]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setConnected(false);
  }, [setConnected]);
  
  // Message action handlers
  const editMessage = useCallback((messageId: string, newContent: string) => {
    return sendMessage('edit_message', { messageId, content: newContent });
  }, [sendMessage]);
  
  const deleteMessage = useCallback((messageId: string) => {
    return sendMessage('delete_message', { messageId });
  }, [sendMessage]);
  
  const regenerateResponse = useCallback((messageId: string) => {
    // Set streaming state before regenerating to prevent duplicate clicks
    setStreaming(true);
    clearStreamContent();
    return sendMessage('regenerate_response', { messageId });
  }, [sendMessage, setStreaming, clearStreamContent]);
  
  const stopGeneration = useCallback(() => {
    return sendMessage('stop_generation');
  }, [sendMessage]);

  // Scroll-back pagination: request the batch of messages strictly older than
  // the oldest one we currently hold. The store handler flips loading state
  // off when the 'older_messages' reply lands.
  const loadOlderMessages = useCallback(() => {
    const state = useActiveWorldStore.getState();
    if (state.isLoadingOlderHistory || !state.hasMoreHistory) return false;
    const oldest = state.messages[0];
    if (!oldest) return false;
    setLoadingOlderHistory(true);
    const ok = sendMessage('load_older_messages', {
      beforeCreatedAt: oldest.created_at,
      limit: 30,
    });
    if (!ok) setLoadingOlderHistory(false);
    return ok;
  }, [sendMessage, setLoadingOlderHistory]);

  return {
    isConnected: useActiveWorldStore(state => state.isConnected),
    sendUserInput,
    requestState,
    disconnect,
    reconnect: disconnect, // Will trigger reconnect via effect
    // Message actions
    editMessage,
    deleteMessage,
    regenerateResponse,
    stopGeneration,
    // History pagination
    loadOlderMessages,
  };
}
